import { GroupsOutlined, PersonOutlined } from '@mui/icons-material';
import { Chip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from '../../components/Providers/AuthProvider';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { getFolder, getFolders, updateFolder } from '../../redux/actions/folderActions';
import { myFolder } from '../../redux/consts';
import { selectFolders, setPage, setSearchQuery } from '../../redux/reducers/folderReducer';
import {
  setSelectedLabels,
  setSortDirection,
  setSortOption,
} from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import {
  FolderView,
  FolderViewConfig,
  IndexType,
  PromptSortOptions,
  SortDirection,
} from '../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useFeatures } from '../Providers/FeatureProvider';
import FolderOptionsMenu from './FolderOptionsMenu/FolderOptionsMenu';
import IntellioAdvantageFolderOptionsMenu from './FolderOptionsMenu/IntellioAdvantageFolderOptionsMenu';
import ShareDialog from './ShareDialog';

const FolderHeader = ({ folderView }: { folderView: FolderView }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const features = useFeatures();
  const { currentFolder } = useSelector(selectFolders);
  const { isAdmin } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);

  const handleShareFolder = useCallback(
    (userIds: string[]) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_SHARED,
        {
          id: currentFolder?.id,
        },
        {
          folderId: currentFolder?.id,
          projectId: currentFolder?.projectId,
          contributors: userIds,
        }
      );
      if (currentFolder) {
        dispatch(
          updateFolder({ folderId: currentFolder.id, updatedFields: { contributors: userIds } })
        ).then(() => {
          dispatch(getFolders());
          dispatch(getFolder({ folderId: currentFolder.id, isAdmin }));
        });
      }
    },
    [dispatch, currentFolder, analytics, isAdmin]
  );

  const availableViews: FolderView[] = useMemo(() => {
    let viewsToHide = [FolderView.CHAT, FolderView.EDIT, FolderView.CONFIG, FolderView.SCOPE_AREAS];

    if (!currentFolder?.projectId) {
      // Nigel
      viewsToHide = [
        ...viewsToHide,
        FolderView.ANALYSIS,
        FolderView.DISCOVERY,
        FolderView.DATA_PROCESSING,
        FolderView.DATA_REQUEST_LIST,
      ];
    }

    if (!features.data_request_list) {
      viewsToHide.push(FolderView.DATA_REQUEST_LIST);
    }

    if (currentFolder?.project?.indexToUse === IndexType.GRAPH_RAG) {
      viewsToHide.push(FolderView.DATA_PROCESSING);
    }

    return Object.values(FolderView)
      .filter((view) => !viewsToHide.includes(view))
      .sort((a, b) => FolderViewConfig[a].order - FolderViewConfig[b].order);
  }, [currentFolder, features.data_request_list]);

  const handleNavigate = useCallback(
    (newView: FolderView) => {
      const base = `/folder/${currentFolder?.id}`;
      const path = FolderViewConfig[newView].path;

      // Reset prompt view filters, sort, and pagination when switching into prompts.
      if (newView === FolderView.PROMPTS) {
        dispatch(setSearchQuery(''));
        dispatch(setSelectedLabels([]));
        dispatch(setSortOption(PromptSortOptions.DEFAULT));
        dispatch(setSortDirection(SortDirection.ASC));
        dispatch(setPage(1));
      }

      // If path is empty, navigate to base (CHATS, CHAT)
      navigate(path ? `${base}/${path}` : base);
    },
    [currentFolder?.id, navigate, dispatch]
  );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginRight: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        {availableViews.map((view) => (
          <Chip
            style={{ padding: '20px 0px' }}
            variant={
              folderView === view ||
              (folderView === FolderView.CHAT && view === FolderView.CHATS) ||
              (folderView === FolderView.SCOPE_AREAS && view === FolderView.ANALYSIS)
                ? 'filled'
                : 'outlined'
            }
            key={view}
            disabled={folderView === FolderView.EDIT}
            label={
              <Typography
                variant="body2"
                sx={
                  folderView === view ||
                  (folderView === FolderView.CHAT && view === FolderView.CHATS) ||
                  (folderView === FolderView.SCOPE_AREAS && view === FolderView.ANALYSIS)
                    ? { color: 'white' }
                    : { color: '#070154' }
                }
              >
                {FolderViewConfig[view].label}
              </Typography>
            }
            onClick={() => {
              analytics.event(
                AnalyticsEvent.WM_NIGEL_FOLDER_VIEW_CHANGE,
                {
                  id: currentFolder?.id,
                  value: view,
                },
                {
                  folderId: currentFolder?.id,
                  projectId: currentFolder?.projectId ?? undefined,
                  folderOldView: folderView,
                  folderNewView: view,
                }
              );

              handleNavigate(view);
            }}
            sx={
              folderView === view ||
              (folderView === FolderView.CHAT && view === FolderView.CHATS) ||
              (folderView === FolderView.SCOPE_AREAS && view === FolderView.ANALYSIS)
                ? {
                    backgroundColor: '#070154',
                    '&:hover': {
                      backgroundColor: '#0047FF',
                    },
                  }
                : { backgroundColor: 'white' }
            }
          />
        ))}
      </div>
      {currentFolder && currentFolder.id !== myFolder.id && (
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {currentFolder.projectId ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <PersonOutlined sx={{ color: '#0047FF' }} />{' '}
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    color: '#0047FF',
                    fontWeight: 'bold',
                  }}
                >
                  Access by Teams
                </Typography>
              </div>
              <IntellioAdvantageFolderOptionsMenu folder={currentFolder} />
            </>
          ) : (
            <>
              <ShareDialog
                ownerId={currentFolder.ownerId}
                currentUsers={currentFolder.contributors}
                isOpen={shareOpen}
                setIsOpen={setShareOpen}
                onShare={handleShareFolder}
              />
              {currentFolder.contributors.length > 0 ? (
                <Chip
                  sx={{ padding: '20px 0px', cursor: 'pointer' }}
                  variant="outlined"
                  onClick={() => setShareOpen(true)}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <GroupsOutlined sx={{ color: '#F900D3' }} />{' '}
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: '#F900D3',
                          fontWeight: 'bold',
                        }}
                      >
                        Shared
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          padding: '2px',
                          color: '#F900D3',
                        }}
                      >
                        +{currentFolder.contributors.length}
                      </Typography>
                    </div>
                  }
                />
              ) : (
                <Chip
                  sx={{ padding: '20px 0px', cursor: 'pointer' }}
                  variant="outlined"
                  onClick={() => setShareOpen(true)}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <PersonOutlined sx={{ color: '#0047FF' }} />{' '}
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: '#0047FF',
                          fontWeight: 'bold',
                        }}
                      >
                        Private
                      </Typography>
                    </div>
                  }
                />
              )}
              <FolderOptionsMenu folder={currentFolder} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderHeader;
